// Pure selection model for one TransferList pane. Kept apart from the
// component so the range rule (the portal's TenantTransferInput) is testable
// and stays O(range) on 25 000 rows.

export interface PaneSelection {
  checked: ReadonlySet<string>
  /** Last plain/Shift click (portal: lastLeftIndex/lastRightIndex), by id. */
  anchor: string | null
}

export type ClickMode = 'single' | 'range' | 'toggle'

export const EMPTY_SELECTION: PaneSelection = { checked: new Set(), anchor: null }

/**
 * Apply a click to `visible` (the pane's rows in their CURRENT, filtered
 * order). `index` maps an id to its position in `visible`.
 *
 * - single: toggle the row, move the anchor to it;
 * - range (Shift): with an anchor in view, check the inclusive range when the
 *   clicked row was unchecked, else uncheck it; the anchor moves to the row
 *   (exactly the portal's rule). Without an anchor in view it acts as single;
 * - toggle (Ctrl/Cmd): toggle the row and keep the anchor.
 */
export function applyClick(
  selection: PaneSelection,
  visible: readonly string[],
  index: ReadonlyMap<string, number>,
  id: string,
  mode: ClickMode
): PaneSelection {
  const isChecking = !selection.checked.has(id)
  const next = new Set(selection.checked)
  const anchorIndex = selection.anchor === null ? undefined : index.get(selection.anchor)
  const at = index.get(id)

  if (mode === 'range' && anchorIndex !== undefined && at !== undefined) {
    const start = Math.min(anchorIndex, at)
    const end = Math.max(anchorIndex, at)
    for (let i = start; i <= end; i++) {
      const rowId = visible[i]
      if (rowId === undefined) continue
      if (isChecking) next.add(rowId)
      else next.delete(rowId)
    }
    return { checked: next, anchor: id }
  }

  if (isChecking) next.add(id)
  else next.delete(id)
  return { checked: next, anchor: mode === 'toggle' ? selection.anchor : id }
}

/** Check every row from `from` to `to` (inclusive), keeping the anchor (Shift+Arrow). */
export function extendTo(
  selection: PaneSelection,
  visible: readonly string[],
  from: number,
  to: number
): PaneSelection {
  const next = new Set(selection.checked)
  const start = Math.max(0, Math.min(from, to))
  const end = Math.min(visible.length - 1, Math.max(from, to))
  for (let i = start; i <= end; i++) {
    const rowId = visible[i]
    if (rowId !== undefined) next.add(rowId)
  }
  return { checked: next, anchor: selection.anchor ?? visible[from] ?? null }
}

/** Check every visible row (Ctrl/Cmd+A). */
export function checkAll(selection: PaneSelection, visible: readonly string[]): PaneSelection {
  const next = new Set(selection.checked)
  for (const id of visible) next.add(id)
  return { checked: next, anchor: selection.anchor }
}
